/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect, useState} from 'react';
import {
  Asset
} from '@ion/core/src/types/entities';
import {
  ServiceError
} from '@ion/core/src/types/services';
import {titleCase} from '@ion/core/src/utils/StringUtil';
import {useShowDeletePrompt} from '@ion/dls/src/hooks/useShowDeletePrompt';
import {DeleteResultPrompt} from '@ion/features/src/prompts/DeleteResultPrompt';
import DeletePrompt from './DeletePrompt';

// defines the UserDeletePrompt Prop type definitions ...
export interface UserDeletePromptProps {
  show: boolean;
  user: Asset | null;
  entity?: string;
  result?: Asset | null;
  loading?: boolean;
  error?: ServiceError | null;
  overrideMessage?: Function | null;
  onDeleteConfirm: (user: Asset) => void;
  onDeleteCancel: () => void;
};

/**
 * @UserDeletePrompt
 *
 * The UserDeletePrompt provides the UI for prompting, confirming, or canceling an User
 * Delete action.
 *
 * ```typescript
 *
 *    import {UserDeletePrompt} from '@ion/dls/src/editors/';
 *
 *    const {
 *      selected,
 *      result,
 *      isEditing,
 *      loading,
 *      error
 *    } = useSelector(({adminAssets: {destroy}}) => destroy);
 *
 *    const onDeleteConfirm = function(user: Asset) {
 *      dispatch(deleteAsset(selected));
 *    };
 *
 *    function onDeleteCancel() {
 *      dispatch(deleteAdminAsset(selected, false));
 *    }
 *
 *    return (
 *      <UserDeletePrompt show={isEditing}
 *        user={selected}
 *        onDeleteConfirm={onDeleteConfirm}
 *        onDeleteCancel={onDeleteCancel}
 *        result={result}
 *        loading={loading}
 *        error={error} />
 *    );
 *
 * ```
 *
 * @param {boolean}       show When true, the user delete prompt will be displayed.
 * @param {User}          user The selected User that is to be deleted.
 * @param {User}          result The User returned by the service after it has been deleted.
 * @param {boolean}       loading When true, indicates that the user is currently being
 *                        deleted.
 * @param {ServiceError}  error A reference to the ServiceError If the deletion fails.
 * @param {Function}      onDeleteConfirm callback function which will be invoked with the
 *                        user when the user confirms the deletion.
 * @param {Function}      onDeleteCancel callback function which will be invoked if the user
 *                        cancels the delete operation.
 */
export const UserDeletePrompt = ({
  show,
  user,
  entity,
  result,
  loading,
  error,
  onDeleteConfirm,
  onDeleteCancel,
  overrideMessage = null
}: UserDeletePromptProps) => {
  const {setShowError, setShowSuccess, showSuccess, showError, closeSnack} = useShowDeletePrompt();

  useEffect(() => {
    setShowError(!!error);
  }, [error]);

  useEffect(() => {
    if (result && !loading) {
      setShowSuccess(true);
    }
  }, [result]);

  if (show || showError || showSuccess) {
    return (
      <>
        {show && (
          <DeletePrompt show
            entity={entity}
            items={user}
            isLoading={loading}
            overrideMessage={overrideMessage}
            onConfirm={function() {
              onDeleteConfirm(user);
            }}
            onCancel={function() {
              onDeleteCancel();
            }} />
        )}
        <DeleteResultPrompt entityTitle={titleCase(entity)}
          error={error}
          isError={showError}
          isSuccess={showSuccess}
          onAction={closeSnack} />
      </>
    );
  };
  return null;
};

export default UserDeletePrompt;
